/**
 * User accessor hooks with different null-handling strategies.
 *
 * - useAuthUser() - throws if no user (for protected contexts)
 * - useSuspendingAuthUser() - suspends until user available (for Suspense boundaries)
 *
 * For nullable user access, use: useAuthStore((s) => s.user)
 */

import type { FrontendUser } from "../types";
import { useAuthStore } from "../store";

/**
 * Get the current user, throwing if not authenticated.
 *
 * Use inside protected routes or components that require a user.
 * Throws a descriptive error if no user is available.
 */
export function useAuthUser(): FrontendUser {
  const user = useAuthStore((s) => s.user);

  if (!user) {
    throw new Error(
      "useAuthUser: No authenticated user. " +
        "Ensure this hook is used inside an auth provider or protected route. " +
        "Use useAuthStore((s) => s.user) if null is expected."
    );
  }

  return user;
}

/**
 * Get the current user, suspending if not yet available.
 *
 * Use with React Suspense boundaries for loading states.
 * The component will suspend until auth initialization completes.
 *
 * The suspense promise is stored in the auth store to ensure proper
 * scoping and avoid issues with HMR/Fast Refresh.
 */
export function useSuspendingAuthUser(): FrontendUser {
  const user = useAuthStore((s) => s.user);

  if (user) {
    return user;
  }

  // No user yet - get or create a suspense promise from the store
  // The promise will be resolved by setAuth when user becomes available
  throw useAuthStore.getState().getSuspensePromise();
}
